import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAuth, setActiveTab } from "../../redux/features/authSlice";
import { IAuthUser } from "../../redux/features/authSlice/authSlice.types";
import { useLocation } from "react-router-dom";
import jwt_decode from "jwt-decode";
import * as XLSX from "xlsx";
import * as FileSaver from "file-saver";
import moment from "moment";
import {
    useCreateParcelMutation,
    useGetDeliveryParcelListQuery,
    useGetPickupParcelListQuery,
    useUpdateParcelMutation,
} from "../../redux/api/parcelApi/parcelApi";
import CustomLoading from "../../components/CustomLoading";
import ParcelFilterCard from "../../components/ParcelFilterCard";
import DataTable from "react-data-table-component";
import { FaEdit } from "react-icons/fa";
import { useGetProfileQuery } from "../../redux/api/profileApi/profileApi";
import { toast } from "react-toastify";
import * as Yup from "yup";
import CustomModal from "../../components/CustomModal";
import { useFormik } from "formik";
import CustomInput from "../../components/CustomInput";
import { useGetStoreListQuery } from "../../redux/api/storeApi/storeApi";
import { useGetDistrictListQuery } from "../../redux/api/districtApi/districtApi";
import { useGetUpazilaListQuery } from "../../redux/api/upazilaApi/upazilaApi";
import { useGetRoadListQuery } from "../../redux/api/roadApi/roadApi";
import CustomDropDownList from "../../components/CustomDropDownList";
import { useOnClickOutside } from "usehooks-ts";

const statusColor = {
    Pending: "bg-[#FF8C00]/10 text-[#FF8C00]",
    Assigned: "bg-[#9ACD32]/10 text-[#9ACD32]",
    "Set Out": "bg-[#FFCE0D]/10 text-[#FFCE0D]",
    Delivered: "bg-[#3CB043]/10 text-[#3CB043]",
    Returning: "bg-[#1D92D8]/10 text-[#1D92D8]",
    Returned: "bg-[#1D92D8]/10 text-[#1D92D8]",
    Failed: "bg-[#161616]/10 text-[#161616]",
    Restored: "bg-[#001780]/10 text-[#001780]",
    Cancelled: "bg-[#C00000]/10 text-[#C00000]",
    Picked: "bg-[#176A73]/10 text-[#176A73]",
    Stored: "bg-[#9ACD32]/10 text-[#9ACD32]",
};

const ParcelList = () => {
    const { token } = useSelector(selectAuth);
    const authData: IAuthUser = token ? jwt_decode(token) : null;
    const { data: profileData, isLoading: profileLoading } = useGetProfileQuery(
        authData?.id
    );
    const permissions: string[] = profileData?.permissions
        ? JSON.parse(profileData?.permissions)
        : [];
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const [showModal, setShowModal] = useState(false);
    const [parcelList, setParcelList] = useState([]);
    const { data: pickupParcelList, isLoading: pickupParcelLoading } =
        useGetPickupParcelListQuery(authData?.id);
    const { data: deliveryParcelList, isLoading: deliveryParcelLoading } =
        useGetDeliveryParcelListQuery(authData?.id);
    const [parcelStatus, setParcelStatus] = useState("All");
    const [parcelId, setParcelId] = useState("");
    const [orderId, setOrderId] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [parcelInfo, setParcelInfo] = useState(null);
    const [selectedStore, setSelectedStore] = useState("");
    const [selectedDistrict, setSelectedDistrict] = useState("");
    const [selectedThana, setSelectedThana] = useState("");
    const [showDropDown, setShowDropDown] = useState(false);
    const { data: storeList, isLoading: storeListLoading } = useGetStoreListQuery(
        authData?.id
    );
    const { data: districtList, isLoading: districtLoading } =
        useGetDistrictListQuery(undefined);
    const { data: upazilaList, isLoading: upazilaLoading } =
        useGetUpazilaListQuery(selectedDistrict);
    const { data: roadList, isLoading: roadLoading } =
        useGetRoadListQuery(selectedThana);

    const [deliveryFee, setDeliveryFee] = useState(0);
    const [createParcel, createResult] = useCreateParcelMutation();
    const [updateParcel, updateResult] = useUpdateParcelMutation();

    const codFee = 0;
    const discount = 0;
    const promoDiscount = 0;
    const additionalCharge = 0;
    const compensationCost = 0;

    const dropDownRef = useRef<HTMLUListElement>();

    useOnClickOutside(dropDownRef, () => setShowDropDown(false));
    const columns = [
        {
            name: "Parcel ID",
            selector: (row) => row?.id,
            sortable: true,
        },

        {
            name: "Order ID",
            selector: (row) => row?.order_id,
            sortable: true,
        },
        {
            name: "Customer Name",
            selector: (row) => row?.customer_details?.split(",")[0],
            sortable: true,
        },
        {
            name: "Customer Phone",
            selector: (row) => row?.customer_details?.split(",")[1],
            sortable: true,
        },
        {
            name: "Customer Address",
            selector: (row) => row?.customer_details?.split(",")?.slice(2)?.join(", "),
            sortable: true,
        },
        {
            name: "Pickup Date",
            selector: (row) => moment(row.pickup_date).format("ll"),
            sortable: true,
        },
        {
            name: "Delivery Date",
            selector: (row) => moment(row.delivery_date).format("ll"),
            sortable: true,
        },
        {
            name: "Collect Amount",
            selector: (row) => "৳ " + row?.collect_amount,
            sortable: true,
        },
        {
            name: "Payment Status",
            selector: (row) => (
                <div
                    className={`py-1 w-20 text-center rounded ${row?.isPaid
                        ? "bg-green-500/10 text-green-500"
                        : "bg-attention/10 text-attention"
                        }`}
                >
                    {row?.isPaid ? "Paid" : "Unpaid"}
                </div>
            ),
            sortable: true,
        },
        {
            name: "Pickup Status",
            selector: (row) => (
                <div
                    className={`py-1 w-20 text-center rounded ${statusColor[row?.pickup_status]
                        }`}
                >
                    {row?.pickup_status}
                </div>
            ),
            sortable: true,
        },
        {
            name: "Delivery Status",
            selector: (row) => (
                <div
                    className={`py-1 w-20 text-center rounded ${statusColor[row?.delivery_status]
                        }`}
                >
                    {row?.delivery_status}
                </div>
            ),
            sortable: true,
        },
        {
            name: "Action",
            cell: (row) => (
                <div
                    onClick={() => {
                        if (permissions?.includes("UpdateParcel")) {
                            if (row?.pickup_status === 'Pending') {
                                setFieldValue(
                                    "customername",
                                    row?.customer_details?.split(",")[0]
                                );
                                setFieldValue(
                                    "customercontactnumber",
                                    row?.customer_details?.split(",")[1]
                                );
                                setFieldValue("deliverydistrict", row?.customer_district);
                                setFieldValue("deliverythana", row?.customer_upazila);
                                setFieldValue("deliveryroad", row?.customer_road);
                                setFieldValue(
                                    "deliveryaddress",
                                    row?.customer_details?.split(",")?.slice(2)?.join(", ")
                                );
                                setFieldValue("pickupaddress", row?.pickup_location);
                                setFieldValue("quantity", row?.quantity);
                                setFieldValue("orderid", row?.order_id);
                                setFieldValue("productcostamount", row?.product_cost_amount);
                                setFieldValue("weightpackage", row?.weight);
                                // setFieldValue("deliveryoption", row?.delivery_option);
                                setFieldValue(
                                    "pickupdate",
                                    new Date(row?.pickup_date).toISOString().split("T")[0]
                                );
                                setFieldValue(
                                    "deliverydate",
                                    new Date(row?.delivery_date).toISOString().split("T")[0]
                                );
                                // setFieldValue("destinatedbranch", row?.d_branch_id);
                                setFieldValue("itemdescriptions", row?.item_description);
                                setFieldValue("deliveryinstructions", row?.delivery_instruction);
                                setParcelInfo(row);
                                setSelectedDistrict(
                                    districtList?.filter(
                                        (item) => item?.name === row?.customer_district
                                    )[0]?.id
                                );
                                setSelectedThana(
                                    upazilaList?.filter(
                                        (item) => item?.name === row?.customer_upazila
                                    )[0]?.id
                                );
                                setShowModal(true);
                            } else {
                                toast.info("You can't edit this parcel! Rider is already assigned for this parcel!");
                            }
                        } else {
                            toast.info("You have not permitted!");
                        }
                    }}
                    className={`flex flex-row gap-2 items-center justify-center py-1 w-20 text-center rounded border ${row?.pickup_status === 'Pending' ? "bg-secondary/10 border-secondary text-secondary hover:bg-secondary" : "bg-warning/10 border-warning text-warning hover:bg-warning"}  cursor-pointer transition-all  hover:animate-pulse hover:text-white`}
                >
                    <FaEdit /> Edit
                </div>
            ),
        },
    ];

    const getTabData = () => {
        let dataList = parcelList;

        if (parcelStatus !== "All") {
            dataList = parcelList?.filter(
                (item) =>
                    item?.delivery_status === parcelStatus ||
                    item?.pickup_status === parcelStatus
            );
        }

        return dataList;
    };

    const getFilteredDataFromPercelList = () => {
        let dataList = getTabData();

        if (parcelId?.length > 0) {
            dataList = dataList.filter((item) => item?.id + "" === parcelId + "");
        }

        if (orderId?.length > 0) {
            dataList = dataList.filter((item) => item?.order_id?.includes(orderId));
        }
        if (phoneNumber?.length > 0) {
            dataList = dataList.filter((item) =>
                item.customer_phone?.includes(phoneNumber)
            );
        }
        if (fromDate && toDate) {
            dataList = dataList.filter((item) => {
                if (item.delivery_date && item.delivery_date !== null) {
                    return moment(item.delivery_date).isBetween(fromDate, toDate);
                }
                return false;
            });
        }
        if (fromDate || toDate) {
            if (fromDate && !toDate) {
                dataList = dataList.filter((item) => {
                    if (item.delivery_date && item.delivery_date !== null) {
                        return moment(item.delivery_date).isSameOrAfter(fromDate);
                    }
                    return false;
                });
            }
            if (!fromDate && toDate) {
                dataList = dataList.filter((item) => {
                    if (item.delivery_date && item.delivery_date !== null) {
                        return moment(item.delivery_date).isSameOrBefore(toDate);
                    }
                    return false;
                });
            }
        }

        return dataList;
    };

    const filteredData = getFilteredDataFromPercelList();

    const parcelDataList = filteredData
        ?.map((item) => {
            let propertiesinItem = {};
            let customerDetails = [];
            Object.keys(item).forEach((key) => {
                if (
                    key === "customer_name" ||
                    key === "customer_address" ||
                    key === "customer_phone"
                ) {
                    customerDetails.push(item[key]);
                } else {
                    propertiesinItem[key] = item[key];
                }
            });
            propertiesinItem["customer_details"] = customerDetails.join(",");

            return propertiesinItem;
        })
        ?.reverse();

    const dataWithUpperCase = filteredData?.map((item) => {
        let propertiesinItem = {};
        Object.keys(item).forEach((key) => {
            if (key !== "created_at" && key !== "updated_at") {
                if (key === "id") {
                    propertiesinItem["Percel Id"] = item[key];
                    return;
                }
                if (key === "pickup_date") {
                    // format date
                    const date =
                        item[key] !== null
                            ? moment(item[key]).format("MMMM Do YYYY, h:mm:ss a")
                            : "";
                    propertiesinItem["Pickup Date"] = date;
                    return;
                }
                if (key === "delivery_date") {
                    // format date
                    propertiesinItem["Delivery Date"] =
                        item[key] !== null
                            ? moment(item[key]).format("MMMM Do YYYY, h:mm:ss a")
                            : "";

                    return;
                }
                let keyName = key.replace(/_/g, " ");
                keyName = keyName.replace(/\w\S*/g, function (txt) {
                    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
                });

                propertiesinItem[keyName] = item[key];
            }
        });
        return propertiesinItem;
    });

    const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    const exportToExcel = (apiData, fileName) => {
        const ws = XLSX.utils.json_to_sheet(apiData);
        const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const data = new Blob([excelBuffer], { type: fileType });
        FileSaver.saveAs(data, fileName + fileExtension);
    };

    const parcelListValidationSchema = Yup.object().shape({
        customername: Yup.string().required("Customer name is required"),
        customercontactnumber: Yup.string().required(
            "Customer contact number is required"
        ),
        deliverydistrict: Yup.string().required("Delivery division is required"),
        deliverythana: Yup.string().required("Delivery thana/upazila is required"),
        deliveryroad: Yup.string().required("Delivery road is required"),
        deliveryaddress: Yup.string().required("Delivery address is required"),
        pickupaddress: Yup.string().required("Pickup address is required"),
        quantity: Yup.number().required("Quantity is required"),
        orderid: Yup.string().required("Order ID is required"),
        productcostamount: Yup.string().required("Amount is required"),
        weightpackage: Yup.number()
            .required("Package weight is required")
            .min(0.1, "Weight must be greater than 0")
            .max(10, "Weight must be less than 10"),
        // deliveryoption: Yup.string().required("Delivery option is required"),
        pickupdate: Yup.string().required("Pickup date is required"),
        deliverydate: Yup.string().required("Delivery date is required"),
        // destinatedbranch: Yup.string().required("Destinated branch is required"),
    });

    const parcelListInitialValues = {
        customername: "",
        customercontactnumber: "",
        deliverydistrict: "",
        deliverythana: "",
        deliveryroad: "",
        deliveryaddress: "",
        pickupaddress: "",
        quantity: "",
        orderid: "",
        productcostamount: '0',
        weightpackage: "",
        // deliveryoption: "Cash On Delivery",
        pickupdate: "",
        deliverydate: "",
        // destinatedbranch: "",
        itemdescriptions: "",
        deliveryinstructions: "",
    };

    const formik = useFormik({
        initialValues: parcelListInitialValues,
        validationSchema: parcelListValidationSchema,
        onSubmit: async (values, { resetForm }) => {
            const data = {
                merchant_id: authData?.id,
                order_id: values?.orderid,
                i_branch_id: profileData?.branch?.id,
                // d_branch_id: branchList?.filter(
                //     (branch) => branch?.name === values?.destinatedbranch
                // )[0]?.id,
                pickup_location: values?.pickupaddress,
                pickup_date: values?.pickupdate,
                delivery_date: values?.deliverydate,
                customer_name: values?.customername,
                customer_phone: values?.customercontactnumber,
                customer_district: values?.deliverydistrict,
                customer_upazila: values?.deliverythana,
                customer_road: values?.deliveryroad,
                customer_address: values?.deliveryaddress,
                collect_amount: parseInt(values?.productcostamount) + deliveryFee,
                product_cost_amount: values?.productcostamount,
                delivery_fees: deliveryFee,
                cod_fees: codFee,
                discount: discount,
                promo_discount: promoDiscount,
                additional_charge: additionalCharge,
                compensation_cost: compensationCost,
                weight: values?.weightpackage,
                quantity: values?.quantity,
                item_description: values?.itemdescriptions,
                delivery_instruction: values?.deliveryinstructions,
            };
            try {
                if (parcelInfo) {
                    await updateParcel({ data, id: parcelInfo?.id }).unwrap();
                    toast.success("Parcel updated successfully!");
                } else {
                    await createParcel(data).unwrap();
                    toast.success("Parcel created successfully!");
                }
                resetForm();
                setShowModal(false);
            } catch (error) {
                console.log(error);
                toast.error(error?.data?.message || "Something went wrong!");
            }
        },
    });

    const {
        handleSubmit,
        values,
        touched,
        errors,
        setFieldValue,
        handleBlur,
        resetForm,
    } = formik;

    console.log(values);

    useEffect(() => {
        if (parseInt(values?.weightpackage) > 2) {
            setDeliveryFee(60 + (parseInt(values?.weightpackage) - 2) * 10);
        }

        if (
            parseInt(values?.weightpackage) >= 1 &&
            parseInt(values?.weightpackage) <= 2
        ) {
            setDeliveryFee(60);
        }
    }, [values?.weightpackage]);

    useEffect(() => {
        if (pickupParcelList && deliveryParcelList) {
            setParcelList([
                ...pickupParcelList,
                ...deliveryParcelList?.filter(
                    (data) => data?.delivery_status !== "Pending"
                ),
            ]);
        }
    }, [pickupParcelList, deliveryParcelList]);

    useEffect(() => {
        if (selectedStore) {
            const pickupAddress = storeList?.filter(
                (store) => store.id + "" === selectedStore
            )[0]?.pickup_address;
            setFieldValue("pickupaddress", pickupAddress);
        } else {
            setFieldValue("pickupaddress", "");
        }
    }, [selectedStore]);

    useEffect(() => {
        if (pathname === "/parcel-list") {
            dispatch(setActiveTab("Parcel List"));
        }
    }, [dispatch, pathname]);

    return (
        <div className="w-full flex flex-col gap-10">
            <ParcelFilterCard
                parcelStatus={parcelStatus}
                setParcelStatus={setParcelStatus}
                parcelId={parcelId}
                setParcelId={setParcelId}
                orderId={orderId}
                setOrderId={setOrderId}
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                exportToExcel={() => {
                    if (permissions?.includes("ExportParcelList")) {
                        if (dataWithUpperCase?.length > 0) {
                            exportToExcel(
                                dataWithUpperCase,
                                `courier-data-${moment().format("DD-MM-YYYY")}`
                            );
                        } else {
                            toast.info("Empty parcel list!");
                        }
                    } else {
                        toast.info("You have not permitted!");
                    }
                }}
                addParcel={() => {
                    if (permissions?.includes("CreateParcel")) {
                        resetForm();
                        setParcelInfo(null);
                        setShowModal(true);
                    } else {
                        toast.info("You have not permitted!");
                    }
                }}
            />
            <div className="w-full overflow-x-auto bg-white shadow-lg rounded-lg p-2">
                <DataTable
                    columns={columns}
                    data={parcelDataList}
                    pagination
                    persistTableHead
                    highlightOnHover
                />
            </div>

            <CustomModal
                showModal={showModal}
                setShowModal={setShowModal}
                submitButton={true}
                handleSubmit={handleSubmit}
                title={parcelInfo ? "Update Parcel Information" : "Create New Parcel"}
            >
                <form
                    onSubmit={handleSubmit}
                    className="w-full flex flex-col gap-2 mt-5 min-w-[70%]"
                >
                    <div className="text-2xl font-bold text-text_1 mb-2">
                        Customer Information
                    </div>
                    <div className="flex flex-col sm:flex-row gap-5">
                        <CustomInput
                            label="CUSTOMER NAME"
                            type="text"
                            setFieldValue={setFieldValue}
                            value={values.customername}
                            touched={touched.customername}
                            error={errors.customername}
                            handleBlur={handleBlur}
                        />
                        <CustomInput
                            label="CUSTOMER CONTACT NUMBER"
                            type="number"
                            setFieldValue={setFieldValue}
                            value={values.customercontactnumber}
                            touched={touched.customercontactnumber}
                            error={errors.customercontactnumber}
                            handleBlur={handleBlur}
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-5">
                        <CustomDropDownList
                            list={districtList}
                            label="DELIVERY DISTRICT"
                            selectTitle="Select a district"
                            onChange={(e) => {
                                setFieldValue("deliverydistrict", e.target.value);
                                setSelectedDistrict(
                                    districtList?.filter(
                                        (item) => item?.name === e.target.value
                                    )[0]?.id
                                );
                            }}
                            value={values.deliverydistrict}
                            touched={touched.deliverydistrict}
                            error={errors.deliverydistrict}
                            handleBlur={handleBlur}
                            required
                        />
                        <CustomDropDownList
                            list={upazilaList}
                            label="DELIVERY THANA"
                            selectTitle="Select a thana/upazila"
                            onChange={(e) => {
                                setFieldValue("deliverythana", e.target.value);
                                setSelectedThana(
                                    upazilaList?.filter(
                                        (item) => item?.name === e.target.value
                                    )[0]?.id
                                );
                            }}
                            value={values.deliverythana}
                            touched={touched.deliverythana}
                            error={errors.deliverythana}
                            handleBlur={handleBlur}
                            required
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-5">
                        <div className="w-full my-2">
                            <div className="text-text_2 text-md font-semibold mb-2">
                                DELIVERY ROAD
                            </div>
                            <div className="relative">
                                <input
                                    id={"deliveryroad"}
                                    value={values.deliveryroad}
                                    onChange={(e) =>
                                        setFieldValue("deliveryroad", e.target.value)
                                    }
                                    className="w-full border-2 p-2 rounded-lg focus:outline-none focus:border-secondary"
                                    onBlur={handleBlur}
                                    onFocus={() => setShowDropDown(true)}
                                />
                                {showDropDown && (
                                    <ul
                                        ref={dropDownRef}
                                        className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-lg shadow-md"
                                    >
                                        {roadList?.map(
                                            (road: { id: number; name: string }, index: number) => (
                                                <li
                                                    key={index}
                                                    className="p-2 cursor-pointer"
                                                    onClick={() => {
                                                        setFieldValue("deliveryroad", road?.name);
                                                        setShowDropDown(false);
                                                    }}
                                                >
                                                    {road?.name}
                                                </li>
                                            )
                                        )}
                                    </ul>
                                )}
                            </div>

                            {touched.deliveryroad && errors.deliveryroad ? (
                                <span className="text-error text-sm mt-1">
                                    {errors.deliveryroad}
                                </span>
                            ) : null}
                        </div>
                        <CustomInput
                            label="DELIVERY ADDRESS"
                            type="text"
                            setFieldValue={setFieldValue}
                            value={values.deliveryaddress}
                            touched={touched.deliveryaddress}
                            error={errors.deliveryaddress}
                            handleBlur={handleBlur}
                        />
                    </div>
                    <div className="text-2xl font-bold text-text_1 my-2">
                        Parcel Information
                    </div>
                    <div className="flex flex-col sm:flex-row gap-5">
                        <div className="w-full my-2">
                            <div className="text-text_2 text-md font-semibold mb-2">
                                SELECT STORE
                            </div>
                            <select
                                id="store"
                                value={selectedStore}
                                onChange={(e) => {
                                    setSelectedStore(e.target.value);
                                }}
                                className="w-full border-2 p-2 rounded-lg focus:outline-none focus:border-secondary"
                            >
                                <option value="">Select a store</option>
                                {storeList?.map((data, index) => {
                                    return (
                                        <option key={index} value={data?.id}>
                                            {data?.name}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        <CustomInput
                            label="PICKUP ADDRESS"
                            type="text"
                            setFieldValue={setFieldValue}
                            value={values.pickupaddress}
                            touched={touched.pickupaddress}
                            error={errors.pickupaddress}
                            handleBlur={handleBlur}
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-5">
                        <CustomInput
                            label="QUANTITY"
                            type="text"
                            setFieldValue={setFieldValue}
                            value={values.quantity}
                            touched={touched.quantity}
                            error={errors.quantity}
                            handleBlur={handleBlur}
                        />
                        <CustomInput
                            label="ORDER ID"
                            type="text"
                            setFieldValue={setFieldValue}
                            value={values.orderid}
                            touched={touched.orderid}
                            error={errors.orderid}
                            handleBlur={handleBlur}
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-5">
                        <CustomInput
                            label="PRODUCT COST AMOUNT"
                            type="number"
                            setFieldValue={setFieldValue}
                            value={values.productcostamount}
                            touched={touched.productcostamount}
                            error={errors.productcostamount}
                            handleBlur={handleBlur}
                        />
                        <CustomInput
                            label="WEIGHT PACKAGE"
                            type="text"
                            setFieldValue={setFieldValue}
                            value={values.weightpackage}
                            touched={touched.weightpackage}
                            error={errors.weightpackage}
                            handleBlur={handleBlur}
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-5">
                        {/* <CustomDropDownList
                            list={deliveryOptions}
                            label="DELIVERY OPTION"
                            selectTitle="Select a delivery option"
                            onChange={(e) => setFieldValue("deliveryoption", e.target.value)}
                            value={values.deliveryoption}
                            touched={touched.deliveryoption}
                            error={errors.deliveryoption}
                            handleBlur={handleBlur}
                            required
                        /> */}
                        <div className="w-full my-2">
                            <div className="text-text_2 text-md font-semibold mb-2">
                                PICKUP DATE
                            </div>
                            <input
                                type="date"
                                id="start"
                                name="trip-start"
                                value={values.pickupdate}
                                onChange={(e) => setFieldValue("pickupdate", e.target.value)}
                                className="w-full border-2 p-2 rounded-lg focus:border-secondary focus:outline-none"
                            />
                            {touched.pickupdate && errors.pickupdate ? (
                                <span className="text-error text-sm mt-1">
                                    {errors.pickupdate}
                                </span>
                            ) : null}
                        </div>

                        <div className="w-full my-2">
                            <div className="text-text_2 text-md font-semibold mb-2">
                                DELIVERY DATE
                            </div>
                            <input
                                type="date"
                                id="start"
                                name="trip-start"
                                value={values.deliverydate}
                                onChange={(e) => setFieldValue("deliverydate", e.target.value)}
                                className="w-full border-2 p-2 rounded-lg focus:border-secondary focus:outline-none"
                            />
                            {touched.deliverydate && errors.deliverydate ? (
                                <span className="text-error text-sm mt-1">
                                    {errors.deliverydate}
                                </span>
                            ) : null}
                        </div>
                    </div>
                    {/* <div className="flex flex-col sm:flex-row gap-5">
                        <div className="w-full my-2">
                            <div className="text-text_2 text-md font-semibold mb-2">
                                DELIVERY DATE
                            </div>
                            <input
                                type="date"
                                id="start"
                                name="trip-start"
                                value={values.deliverydate}
                                onChange={(e) => setFieldValue("deliverydate", e.target.value)}
                                className="w-full border-2 p-2 rounded-lg focus:border-secondary focus:outline-none"
                            />
                            {touched.deliverydate && errors.deliverydate ? (
                                <span className="text-error text-sm mt-1">
                                    {errors.deliverydate}
                                </span>
                            ) : null}
                        </div>
                        <CustomDropDownList
                            list={branchList}
                            label="DESTINATED BRANCH"
                            selectTitle="Select a branch"
                            onChange={(e) =>
                                setFieldValue("destinatedbranch", e.target.value)
                            }
                            value={values.destinatedbranch}
                            touched={touched.destinatedbranch}
                            error={errors.destinatedbranch}
                            handleBlur={handleBlur}
                            required
                        />
                    </div> */}
                    <div className="flex flex-col sm:flex-row gap-5">
                        <CustomInput
                            label="ITEM DESCRIPTIONS"
                            type="text"
                            setFieldValue={setFieldValue}
                            value={values.itemdescriptions}
                            touched={touched.itemdescriptions}
                            error={errors.itemdescriptions}
                            handleBlur={handleBlur}
                        />
                        <CustomInput
                            label="DELIVERY INSTRUCTIONS"
                            type="text"
                            setFieldValue={setFieldValue}
                            value={values.deliveryinstructions}
                            touched={touched.deliveryinstructions}
                            error={errors.deliveryinstructions}
                            handleBlur={handleBlur}
                        />
                    </div>
                </form>
                <div className="bg-[#F4F4F4] rounded-lg px-2 py-3 mt-4 flex flex-col gap-2">
                    <div className="text-lg text-text_1 font-bold m-2">
                        Cost Of Delivary
                    </div>
                    <div className="bg-white rounded-lg p-3 flex justify-between items-center text-md font-semibold text-text_1">
                        <div>Product Cost Amount</div>
                        <div>৳ {values?.productcostamount}</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 flex justify-between items-center text-md font-semibold text-text_1">
                        <div>Delivary Fee</div>
                        <div>৳ {deliveryFee}</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 flex justify-between items-center text-md font-semibold text-text_1">
                        <div>COD Fee</div>
                        <div>৳ {codFee}</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 flex justify-between items-center text-md font-semibold text-text_1">
                        <div>Discount</div>
                        <div>৳ {discount}</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 flex justify-between items-center text-md font-semibold text-text_1">
                        <div>Promo Discount</div>
                        <div>৳ {promoDiscount}</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 flex justify-between items-center text-md font-semibold text-text_1">
                        <div>Additional Charge</div>
                        <div>৳ {additionalCharge}</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 flex justify-between items-center text-md font-semibold text-text_1">
                        <div>Compensation Cost</div>
                        <div>৳ {compensationCost}</div>
                    </div>
                    <div className="border-b-2 border-secondary mt-5" />
                    <div className="p-3 flex justify-between items-center text-lg font-semibold text-text_1">
                        <div>Total Amount</div>
                        <div>
                            ৳{" "}
                            {parseInt(values.productcostamount) +
                                deliveryFee +
                                codFee -
                                discount -
                                promoDiscount +
                                additionalCharge +
                                compensationCost}
                        </div>
                    </div>
                </div>
            </CustomModal>

            {(pickupParcelLoading ||
                deliveryParcelLoading ||
                profileLoading ||
                districtLoading ||
                upazilaLoading ||
                roadLoading ||
                storeListLoading || createResult.isLoading || updateResult.isLoading) && <CustomLoading />}
        </div>
    );
};

export default ParcelList;
